import { type RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as THREE from "three";
import { motion } from "framer-motion";
import {
  ArrowLeft, Tag as TagIcon, Ruler, MousePointer, X, Loader2, Cog, Maximize2, Keyboard, ZoomIn, ZoomOut, Orbit, Plane, Trash2, Pencil, ExternalLink, Radio, Home,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { useAuthStore } from "../stores/authStore";
import { useAppStore } from "../stores/appStore";
import { canAdminOrg } from "../lib/permissions";
import {
  type DigitalTwin,
  type DigitalTwinTag,
  deleteTag,
  getDigitalTwin,
  getViewUrl,
  listTagsForDigitalTwin,
  reprocessDigitalTwin,
} from "../lib/digitalTwinsApi";
import { SparkViewer, type SparkViewerHandle, type ViewerMode } from "../components/digital-twins/SparkViewer";
import { PlaceTagModal } from "../components/digital-twins/PlaceTagModal";
import { EditTagModal } from "../components/digital-twins/EditTagModal";
import { PlaceSensorTagModal } from "../components/instrumentation/PlaceSensorTagModal";
import { SensorTagsList } from "../components/instrumentation/SensorTagsList";
import { type AssetOption } from "../components/instrumentation/SearchableAssetPicker";
import {
  type SensorTag,
  deleteSensorTag,
  listSensorTags,
} from "../lib/instrumentationApi";
import api from "../lib/api";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import {
  type AssetMaintenanceSummary,
  MAINTENANCE_STATE_TOKENS,
  fetchSiteMaintenanceByAsset,
} from "../lib/maintenanceApi";
import { formatDate } from "../lib/utils";

type Tool = "navigate" | "tag" | "measure" | "sensor";

interface MeasurePoint {
  pos: THREE.Vector3;
  // SparkViewer.addPointMarker returns a Sprite (an Object3D, NOT a Mesh).
  // Typed as Object3D here so the return value plugs in without a cast.
  marker: THREE.Object3D;
}

export default function DigitalTwinViewer() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Tag id deep-linked via `?tag=…` (e.g. from the asset detail page's
  // "Open in 3D" button). Auto-selected and focused once the splat is ready.
  const requestedTagId = searchParams.get("tag");
  // Sensor-tag id deep-linked via `?sensor_tag=…` — issued by the
  // SensorDetailDrawer "View in 3D" link so the camera flies to the sensor
  // marker instead of the splat's default view.
  const requestedSensorTagId = searchParams.get("sensor_tag");
  const autoFocusedRef = useRef(false);
  const autoFocusedSensorRef = useRef(false);
  const viewerRef = useRef<SparkViewerHandle>(null);
  const { user, organizations } = useAuthStore();
  const { selectedOrgId } = useAppStore();
  const isAdmin = canAdminOrg(user, organizations, selectedOrgId);

  const [twin, setTwin] = useState<DigitalTwin | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [tags, setTags] = useState<DigitalTwinTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // Splat download/parse tracking. `progress` is the bytes streamed from S3
  // (total = 0 when the server omits Content-Length — we then show an
  // indeterminate shimmer instead of a percentage). `splatReady` flips true
  // once the splat's bounding box becomes valid, which is when the first
  // visible frame renders.
  const [splatProgress, setSplatProgress] = useState<{ loaded: number; total: number }>({ loaded: 0, total: 0 });
  const [splatReady, setSplatReady] = useState(false);

  const [tool, setTool] = useState<Tool>("navigate");
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewerMode>("orbit");
  const [fov, setFov] = useState(75);
  const [pendingPick, setPendingPick] = useState<{ x: number; y: number; z: number } | null>(null);
  const [measurePoints, setMeasurePoints] = useState<MeasurePoint[]>([]);
  const measureLineRef = useRef<THREE.Line | null>(null);
  // Brief toast shown when a tag/measure click misses the splat surface.
  const [missNotice, setMissNotice] = useState<string | null>(null);
  const missTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks whether the splat viewer's wasm raycast is callable yet. Rendering
  // is up before this — sort-worker wasm loads first — so a click during this
  // window needs a different message than "you clicked off the model."
  const [raycastStatus, setRaycastStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  // Map of tag.id → its rendered marker object (a Sprite in current builds).
  // Kept in a ref so the markers effect can diff against the previous render
  // without re-creating meshes for tags that are already on-screen.
  const tagMarkersRef = useRef<Map<string, THREE.Object3D>>(new Map());
  // Sensor tags get their own marker map so the asset/sensor diffs don't
  // collide on shared ids. Sensor markers are rendered in a distinct color
  // (cyan default) and clicking one opens the SensorDataPanel.
  const sensorMarkersRef = useRef<Map<string, THREE.Object3D>>(new Map());
  const [sensorTags, setSensorTags] = useState<SensorTag[]>([]);
  const [pendingSensorPick, setPendingSensorPick] = useState<{ x: number; y: number; z: number } | null>(null);
  const [selectedSensorTagId, setSelectedSensorTagId] = useState<string | null>(null);
  // Which list the right sidebar shows. Driven by the top toolbar's tag
  // buttons (Asset Tag → assets, Sensor Tag → sensors) so the sidebar
  // always reflects the user's current intent.
  const [sidebarTab, setSidebarTab] = useState<"assets" | "sensors">("assets");
  const [deletingSensorTagId, setDeletingSensorTagId] = useState<string | null>(null);
  // Tracks the "viewer key" we last rendered markers against. When viewUrl or
  // viewMode changes the SparkViewer disposes its markers group, so the
  // meshes we previously stored in tagMarkersRef are stale (orphaned). We
  // detect that here and clear the ref *without* calling removeMarker (the
  // group is already gone — calling it would throw or no-op).
  const lastViewerKey = useRef<string>("");

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;
    setIsLoading(true);
    setLoadError(null);

    async function load() {
      try {
        const [dt, tagList] = await Promise.all([
          getDigitalTwin(id!),
          listTagsForDigitalTwin(id!),
        ]);
        if (cancelled) return;
        setTwin(dt);
        setTags(tagList.tags || []);

        if (dt.status === "processing") {
          // Conversion still running; the view URL would point at the source
          // .ply (slow). Wait for status to flip to "ready" then refetch.
          setViewUrl(null);
          setLoadError(null);
          setIsLoading(false);
          pollTimer = setTimeout(load, 5000);
          return;
        }

        if (dt.status === "error") {
          setLoadError("Conversion failed on the server. Re-upload the source file.");
          setIsLoading(false);
          return;
        }

        const view = await getViewUrl(id!);
        if (cancelled) return;
        // Reset splat-load state for the new URL so the progress overlay
        // re-mounts and counts up from 0 instead of inheriting the last
        // load's bytes/ready flag.
        setSplatProgress({ loaded: 0, total: 0 });
        setSplatReady(false);
        setViewUrl(view.view_url);
        if (!view.view_url) {
          setLoadError(
            "The backend is in dev mode (no AWS credentials), so no presigned URL was issued. The viewer needs real S3 access to load the splat.",
          );
        }
      } catch (err: unknown) {
        if (cancelled) return;
        const e = err as { response?: { status?: number; data?: { detail?: string } } };
        if (e.response?.status === 404) {
          setLoadError("Digital twin not found, or you don't have access to this site.");
        } else {
          setLoadError(e.response?.data?.detail || "Failed to load digital twin.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [id]);

  const refreshTags = useCallback(() => {
    if (!id) return;
    listTagsForDigitalTwin(id).then((res) => setTags(res.tags || [])).catch(() => {});
  }, [id]);

  const refreshSensorTags = useCallback(() => {
    if (!id) return;
    listSensorTags({ digital_twin_id: id })
      .then((res) => setSensorTags(res))
      .catch(() => setSensorTags([]));
  }, [id]);

  useEffect(() => {
    refreshSensorTags();
  }, [refreshSensorTags]);

  // Same-site assets for the searchable picker rendered inside the
  // expanded sensor-tag row. Pulled once per twin since the asset list is
  // bounded by the virtual site.
  const [siteAssets, setSiteAssets] = useState<AssetOption[]>([]);
  useEffect(() => {
    const siteId = twin?.virtual_site_id;
    if (!siteId) {
      setSiteAssets([]);
      return;
    }
    let cancelled = false;
    api
      .get("/assets", { params: { virtual_site_id: siteId, page_size: 500 } })
      .then((res) => {
        if (cancelled) return;
        setSiteAssets((res.data?.items || []) as AssetOption[]);
      })
      .catch((err) => {
        console.error("Failed to load assets for sensor-tag asset picker:", err);
        if (!cancelled) setSiteAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [twin?.virtual_site_id]);

  // Per-asset maintenance summary keyed by asset_id. Built from a single
  // /maintenance/tasks fetch scoped to this twin's site so the tag list can
  // overlay overdue / in-progress / scheduled indicators without an N+1 lookup.
  // Refetched whenever the underlying tag list changes (placing or removing a
  // tag doesn't move maintenance, but new tag → new asset that we now want
  // status for, and the list of tags effectively bounds which assets matter).
  const [maintenanceByAsset, setMaintenanceByAsset] = useState<
    Map<string, AssetMaintenanceSummary>
  >(new Map());
  const refreshMaintenance = useCallback(() => {
    const siteId = twin?.virtual_site_id;
    if (!siteId) return;
    fetchSiteMaintenanceByAsset(siteId)
      .then(setMaintenanceByAsset)
      .catch(() => setMaintenanceByAsset(new Map()));
  }, [twin?.virtual_site_id]);
  useEffect(() => {
    refreshMaintenance();
  }, [refreshMaintenance, tags]);

  /** Sidebar tag click → animate the camera onto the tag's 3D position and
   *  pulse its marker. Replaces the previous behaviour (navigate to the asset
   *  detail page), which the user found jarring — the whole point of being in
   *  the viewer is to inspect the model. */
  const focusTag = useCallback((tag: DigitalTwinTag) => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.zoomToPoint(new THREE.Vector3(x, y, z));
    const marker = tagMarkersRef.current.get(tag.id);
    if (marker) viewer.highlightMarker(marker);
  }, []);

  /** Same as focusTag but for sensor tags — they share the geometry shape
   *  but live in a separate marker map. */
  const focusSensorTag = useCallback((tag: SensorTag) => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.zoomToPoint(new THREE.Vector3(x, y, z));
    const marker = sensorMarkersRef.current.get(tag.id);
    if (marker) viewer.highlightMarker(marker);
  }, []);

  const [deletingTagId, setDeletingTagId] = useState<string | null>(null);
  // The tag that's currently expanded inline in the sidebar AND has its 3D
  // label overlay drawn on the canvas. Set by clicking either the sidebar
  // row or the 3D marker; cleared when the user clicks an empty area of the
  // splat. Only one tag is selected at a time.
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  // Tag-deletion is now a two-step flow driven by an in-app dialog instead
  // of window.confirm: clicking the trash icon stages the tag here, the
  // ConfirmDialog renders, and the actual DELETE only fires once the user
  // hits the destructive button in the dialog.
  const [tagPendingDelete, setTagPendingDelete] = useState<DigitalTwinTag | null>(null);
  const [tagDeleteError, setTagDeleteError] = useState<string | null>(null);
  const [tagBeingEdited, setTagBeingEdited] = useState<DigitalTwinTag | null>(null);

  // Lists derived from the current tag set, passed into the place/edit
  // modals. Hides already-tagged assets from the dropdowns (matches the
  // backend's one-tag-per-asset uniqueness rule) and seeds the random-
  // color picker so consecutive new tags get distinct colors.
  const taggedAssetIds = useMemo(() => tags.map((t) => t.asset_id), [tags]);
  const usedTagColors = useMemo(() => tags.map((t) => t.color), [tags]);

  const handleDeleteTag = useCallback((tag: DigitalTwinTag) => {
    setTagDeleteError(null);
    setTagPendingDelete(tag);
  }, []);

  // Deep-link auto-focus: once the splat is ready and the requested tag's
  // marker has been created by the markers effect, animate the camera onto
  // it and select it in the sidebar. Guarded by autoFocusedRef so a later
  // tag-list refresh doesn't re-fire this and yank the camera back.
  useEffect(() => {
    if (autoFocusedRef.current) return;
    if (!requestedTagId || !splatReady || tags.length === 0) return;
    const tag = tags.find((t) => t.id === requestedTagId);
    if (!tag) return;
    // Two RAFs: the markers effect schedules its add via requestAnimationFrame,
    // so we wait an extra frame to be sure the marker mesh is in the map
    // before highlightMarker is called.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoFocusedRef.current = true;
        setSelectedTagId(tag.id);
        focusTag(tag);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [requestedTagId, splatReady, tags, focusTag]);

  // Sensor-tag deep-link auto-focus — same shape as the asset-tag handler
  // above but waits on sensorTags and flips the sidebar to the Sensors tab
  // so the focused tag is visible in the side panel the user came in to see.
  useEffect(() => {
    if (autoFocusedSensorRef.current) return;
    if (!requestedSensorTagId || !splatReady || sensorTags.length === 0) return;
    const tag = sensorTags.find((t) => t.id === requestedSensorTagId);
    if (!tag) return;
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        autoFocusedSensorRef.current = true;
        setSidebarTab("sensors");
        setSelectedSensorTagId(tag.id);
        focusSensorTag(tag);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [requestedSensorTagId, splatReady, sensorTags, focusSensorTag]);

  const cancelTagDelete = useCallback(() => {
    if (deletingTagId) return; // mid-delete, don't allow cancel
    setTagPendingDelete(null);
    setTagDeleteError(null);
  }, [deletingTagId]);

  const confirmTagDelete = useCallback(async () => {
    const tag = tagPendingDelete;
    if (!tag) return;
    try {
      setDeletingTagId(tag.id);
      setTagDeleteError(null);
      await deleteTag(tag.id);
      refreshTags();
      setTagPendingDelete(null);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setTagDeleteError(e.response?.data?.detail || "Failed to delete tag.");
    } finally {
      setDeletingTagId(null);
    }
  }, [tagPendingDelete, refreshTags]);

  // Render existing tags as 3D markers on the splat. Runs every time the
  // tags array, view URL, or view mode changes (mode rebuilds the scene's
  // markers group via SparkViewer's mode effect — so we re-add ours too).
  //
  // The map diff keeps marker meshes stable across tag list refreshes (e.g.
  // after adding a new tag); only newly-arrived tags get fresh meshes and
  // only removed tags get torn down.
  useEffect(() => {
    if (!viewUrl || !viewerRef.current) return;
    const viewer = viewerRef.current;
    const current = tagMarkersRef.current;

    // Wait one frame so the SparkViewer has set up its markers group after a
    // mount or mode swap. Without this, addPointMarker can fire before the
    // group exists and silently no-op.
    const raf = requestAnimationFrame(() => {
      // Detect viewer rebuild (mode toggle or URL swap) and abandon the old
      // mesh references — they belong to a markers group that's been torn
      // down and removeMarker on them is a no-op or worse.
      const viewerKey = `${viewUrl}|${viewMode}`;
      if (lastViewerKey.current !== viewerKey) {
        tagMarkersRef.current.clear();
        lastViewerKey.current = viewerKey;
      }

      const seen = new Set<string>();
      for (const tag of tags) {
        const g = (tag.geometry || {}) as Record<string, unknown>;
        const x = g.x as number, y = g.y as number, z = g.z as number;
        if (tag.tag_type !== "point" || typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
          continue;
        }
        seen.add(tag.id);
        if (!current.has(tag.id)) {
          const color = parseTagColor(tag.color);
          // Asset tags render as cubes in the user-picked color. Distinct
          // from the sensor-tag spheres so the two layers don't blur into
          // each other on a dense splat.
          const obj = viewer.addPointMarker(
            new THREE.Vector3(x, y, z),
            color,
            { shape: "cube" },
          );
          // Stash the tagId so SparkViewer's pointer-up marker raycast can
          // resolve the picked object back to its tag without needing the
          // parent's tagMarkers map.
          obj.userData.tagId = tag.id;
          current.set(tag.id, obj);
        }
      }
      // Remove markers for tags that are no longer in the list.
      for (const [tagId, mesh] of current.entries()) {
        if (!seen.has(tagId)) {
          viewer.removeMarker(mesh);
          current.delete(tagId);
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [tags, viewUrl, viewMode]);

  // Sensor tag markers — separate from asset markers so the two ID spaces
  // don't collide, and rendered with the sensor's color (defaults to cyan)
  // to keep them visually distinct from asset tags. Each marker stashes its
  // sensorTagId so the marker-pick handler can resolve a click back to the
  // sensor row.
  useEffect(() => {
    if (!viewUrl || !viewerRef.current) return;
    const viewer = viewerRef.current;
    const current = sensorMarkersRef.current;
    const raf = requestAnimationFrame(() => {
      // The asset markers effect already clears its map on viewer rebuild;
      // do the same here keyed off the shared viewerKey.
      const viewerKey = `${viewUrl}|${viewMode}|sensors`;
      if (lastViewerKey.current !== viewerKey) {
        // Note: lastViewerKey is also touched by the asset markers effect.
        // The shared write is fine — both effects re-add their markers on
        // the same frame after a rebuild.
        sensorMarkersRef.current.clear();
        lastViewerKey.current = viewerKey;
      }
      const seen = new Set<string>();
      for (const tag of sensorTags) {
        const g = (tag.geometry || {}) as Record<string, unknown>;
        const x = g.x as number, y = g.y as number, z = g.z as number;
        if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") continue;
        seen.add(tag.id);
        if (!current.has(tag.id)) {
          const color = parseTagColor(tag.color, 0x22d3ee);
          // Sensor tags render as spheres in the tag's color — round/soft
          // shape stays distinct from the asset-tag cubes at any angle.
          const obj = viewer.addPointMarker(
            new THREE.Vector3(x, y, z),
            color,
            { shape: "sphere" },
          );
          obj.userData.sensorTagId = tag.id;
          current.set(tag.id, obj);
        }
      }
      for (const [tagId, mesh] of current.entries()) {
        if (!seen.has(tagId)) {
          viewer.removeMarker(mesh);
          current.delete(tagId);
        }
      }
    });
    return () => cancelAnimationFrame(raf);
  }, [sensorTags, viewUrl, viewMode]);

  function resetMeasure() {
    measurePoints.forEach((p) => viewerRef.current?.removeMarker(p.marker));
    if (measureLineRef.current) {
      viewerRef.current?.removeLine(measureLineRef.current);
      measureLineRef.current = null;
    }
    setMeasurePoints([]);
  }

  function handlePick(point: THREE.Vector3 | null) {
    // Any click that reaches the splat (not a marker hit) deselects the
    // current tag — that's the "click away from the tag" behaviour. We do
    // this before the tool dispatch so it applies in every mode.
    if (selectedTagId) setSelectedTagId(null);
    if (selectedSensorTagId) setSelectedSensorTagId(null);
    if (!point) {
      // The pick came back null. Three possible reasons:
      //   - raycast wasm hasn't probed-as-ready yet (very early click)
      //   - wasm probe gave up entirely (bundling/spark issue)
      //   - the click was actually off the model surface
      // The toast wording reflects which case we're in so the user can
      // either retry, refresh, or aim differently.
      if (tool === "tag" || tool === "measure") {
        let msg: string;
        if (raycastStatus === "loading") {
          msg = "3D engine still initializing — try again in a moment.";
        } else if (raycastStatus === "unavailable") {
          msg = "3D picking is unavailable in this session. Try a hard refresh (Ctrl+Shift+R).";
        } else {
          msg = "Click landed off the model — try a denser area of the splat.";
        }
        setMissNotice(msg);
        if (missTimerRef.current) clearTimeout(missTimerRef.current);
        missTimerRef.current = setTimeout(() => setMissNotice(null), 3000);
      }
      return;
    }
    setMissNotice(null);
    if (tool === "tag") {
      setPendingPick({ x: point.x, y: point.y, z: point.z });
    } else if (tool === "sensor") {
      setPendingSensorPick({ x: point.x, y: point.y, z: point.z });
    } else if (tool === "measure") {
      const marker = viewerRef.current?.addPointMarker(point, 0x10b981);
      if (!marker) return;
      const next: MeasurePoint = { pos: point.clone(), marker };
      if (measurePoints.length === 0) {
        setMeasurePoints([next]);
      } else if (measurePoints.length === 1) {
        const line = viewerRef.current?.addLine(measurePoints[0].pos, point, 0x10b981);
        if (line) measureLineRef.current = line;
        setMeasurePoints([measurePoints[0], next]);
      } else {
        // Third click — start a new measurement
        resetMeasure();
        const fresh = viewerRef.current?.addPointMarker(point, 0x10b981);
        if (fresh) setMeasurePoints([{ pos: point.clone(), marker: fresh }]);
      }
    }
  }

  const measureDistance = useMemo(() => {
    if (measurePoints.length < 2) return null;
    return measurePoints[0].pos.distanceTo(measurePoints[1].pos);
  }, [measurePoints]);

  const selectedTag = useMemo(
    () => (selectedTagId ? tags.find((t) => t.id === selectedTagId) ?? null : null),
    [selectedTagId, tags],
  );
  const selectedSensorTag = useMemo(
    () => (selectedSensorTagId ? sensorTags.find((t) => t.id === selectedSensorTagId) ?? null : null),
    [selectedSensorTagId, sensorTags],
  );
  const taggedSensorIds = useMemo(() => sensorTags.map((t) => t.sensor_id), [sensorTags]);

  // Marker click — fires before the canvas's splat pick. Selecting a tag
  // here drives both the label overlay AND the sidebar expansion. The same
  // handler also resolves sensor-tag markers via the parallel sensorTagId
  // userData key.
  const handleMarkerPick = useCallback((marker: THREE.Object3D) => {
    const tagId = marker.userData.tagId as string | undefined;
    const sensorTagId = marker.userData.sensorTagId as string | undefined;
    if (sensorTagId) {
      setSelectedSensorTagId(sensorTagId);
      const tag = sensorTags.find((t) => t.id === sensorTagId);
      if (tag) {
        const g = tag.geometry as { x?: number; y?: number; z?: number };
        if (typeof g.x === "number" && typeof g.y === "number" && typeof g.z === "number") {
          viewerRef.current?.zoomToPoint(new THREE.Vector3(g.x, g.y, g.z));
          viewerRef.current?.highlightMarker(marker);
        }
      }
      return;
    }
    if (!tagId) return;
    setSelectedTagId(tagId);
    const tag = tags.find((t) => t.id === tagId);
    if (tag) focusTag(tag);
  }, [tags, sensorTags, focusTag]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="-mx-6 -my-6 flex h-[calc(100vh-4rem)] flex-col bg-surface-950"
    >
      {/* Header bar — three-column grid so the tool switcher stays centered
          horizontally regardless of the title's length. min-w-0 on the side
          columns lets the title truncate without pushing the tools off-axis. */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 border-b border-surface-700/50 bg-surface-900/60 px-6 py-3 backdrop-blur-xl">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon-sm" onClick={() => navigate("/digital-twins")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold text-surface-100">
              {twin?.name ?? (isLoading ? "Loading…" : "Digital Twin")}
            </h1>
            {twin && (
              <div className="flex items-center gap-2 text-xs text-surface-400">
                <span>{tags.length} asset tag{tags.length === 1 ? "" : "s"}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tool switcher — workflow choices (mutually exclusive with
            navigate), not camera controls. Camera controls live in the
            bottom-right floating stack. */}
        <div className="liquid-glass flex items-center gap-1 rounded-xl p-1">
          <ToolButton active={tool === "navigate"} onClick={() => { setTool("navigate"); resetMeasure(); }} icon={MousePointer} label="Navigate" />
          <ToolButton
            active={tool === "tag"}
            onClick={() => {
              setTool("tag");
              setSidebarTab("assets");
              resetMeasure();
            }}
            icon={TagIcon}
            label="Asset Tag"
          />
          <ToolButton active={tool === "measure"} onClick={() => { setTool("measure"); resetMeasure(); }} icon={Ruler} label="Measure Tag" wip />
          {/* Sensor Tag tool — placing a sensor on the 3D model. Clicking on
              the splat surface opens the device/sensor picker; existing
              sensor markers can be clicked to open the live data panel.
              Also flips the right sidebar to the Sensor Tags list. */}
          <ToolButton
            active={tool === "sensor"}
            onClick={() => {
              setTool("sensor");
              setSidebarTab("sensors");
              resetMeasure();
            }}
            icon={Radio}
            label="Sensor Tag"
          />
        </div>

        {/* Right spacer column — kept empty so the centered tools stay
            optically balanced against the left title block. */}
        <div />
      </div>

      {/* Canvas + side panel */}
      <div className="flex flex-1 overflow-hidden">
        <div className="relative flex-1">
          {/* Loading overlay — covers the splat through both phases:
               1. metadata + view-URL fetch (isLoading=true)
               2. splat download + parse (isLoading=false, splatReady=false)
              Without this second phase the user sees the spinner vanish a
              few seconds before the splat actually appears, which feels
              broken. Now the overlay stays until SparkViewer signals
              onSplatReady (first valid bounding-box frame). */}
          {(isLoading || (!splatReady && !loadError && twin?.status === "ready")) && (
            <LoadingOverlay
              phase={isLoading ? "metadata" : splatProgress.loaded === 0 ? "connecting" : splatProgress.total > 0 && splatProgress.loaded >= splatProgress.total ? "parsing" : "downloading"}
              loaded={splatProgress.loaded}
              total={splatProgress.total}
            />
          )}
          {loadError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-950">
              <div className="max-w-md rounded-xl border border-yellow-500/30 bg-yellow-500/10 px-6 py-4 text-center text-sm text-yellow-200">
                <Cog className="mx-auto mb-2 h-6 w-6" />
                <div>{loadError}</div>
                {twin?.status === "error" && isAdmin && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    isLoading={isReprocessing}
                    onClick={async () => {
                      if (!id) return;
                      try {
                        setIsReprocessing(true);
                        const updated = await reprocessDigitalTwin(id);
                        setTwin(updated);
                        setLoadError(null);
                      } catch (err: unknown) {
                        const e = err as { response?: { data?: { detail?: string } } };
                        setLoadError(e.response?.data?.detail || "Reprocess failed.");
                      } finally {
                        setIsReprocessing(false);
                      }
                    }}
                  >
                    Retry conversion
                  </Button>
                )}
              </div>
            </div>
          )}
          {twin?.status === "processing" && !loadError && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-950">
              <div className="max-w-md rounded-xl border border-brand-500/30 bg-brand-500/10 px-6 py-5 text-center text-sm text-brand-100">
                <Loader2 className="mx-auto mb-2 h-6 w-6 animate-spin text-brand-300" />
                <p className="font-medium">Optimizing splat for faster loading…</p>
                <p className="mt-1 text-xs text-brand-200/80">
                  Converting source to .spz (5-10&times; smaller). This usually takes 30 seconds to a few minutes.
                </p>
              </div>
            </div>
          )}
          {viewUrl && !loadError && (
            <SparkViewer
              ref={viewerRef}
              viewUrl={viewUrl}
              mode={viewMode}
              onPick={handlePick}
              onMarkerPick={handleMarkerPick}
              onLoadError={(msg) => setLoadError(msg)}
              onStatus={(event) => {
                if (event === "raycast-ready") setRaycastStatus("ready");
                if (event === "raycast-unavailable") setRaycastStatus("unavailable");
              }}
              onLoadProgress={(loaded, total) => setSplatProgress({ loaded, total })}
              onSplatReady={() => setSplatReady(true)}
              className={
                "h-full w-full " +
                (tool === "tag" || tool === "measure" || tool === "sensor"
                  ? "cursor-crosshair"
                  : "cursor-grab active:cursor-grabbing")
              }
            />
          )}

          {/* Selected-tag overlay — drawn as a separate component that
              updates its own DOM via refs in a rAF loop, so the per-frame
              tracking doesn't re-render the rest of the viewer. */}
          {selectedTag && (
            <TagLabelOverlay
              key={selectedTag.id}
              tag={selectedTag}
              viewerRef={viewerRef}
              maintenance={maintenanceByAsset.get(selectedTag.asset_id) ?? null}
            />
          )}

          {/* Same leader-line + label treatment for the selected sensor
              tag. Renders in parallel with the asset overlay so picking
              both an asset tag and a sensor tag is non-destructive. */}
          {selectedSensorTag && (
            <SensorTagLabelOverlay
              key={selectedSensorTag.id}
              tag={selectedSensorTag}
              viewerRef={viewerRef}
            />
          )}

          {/* Measure overlay */}
          {tool === "measure" && measurePoints.length > 0 && (
            <div className="liquid-glass absolute bottom-4 left-4 rounded-xl px-4 py-3 text-sm text-surface-100 shadow-xl">
              <div className="flex items-center gap-2">
                <Ruler className="h-4 w-4 text-emerald-400" />
                <span className="font-medium">Measurement</span>
                <button onClick={resetMeasure} className="ml-2 text-surface-400 hover:text-surface-200">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              <div className="mt-1 text-xs text-surface-400">
                {measurePoints.length === 1
                  ? "Click a second point on the splat"
                  : measureDistance != null
                  ? <span className="text-base font-semibold text-emerald-300">{measureDistance.toFixed(3)} units</span>
                  : null}
              </div>
            </div>
          )}

          {/* Tool hint */}
          {tool === "tag" && !pendingPick && !isLoading && !loadError && !missNotice && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full px-4 py-2 text-xs text-surface-300">
              Click directly on the model surface to place a tag
            </div>
          )}

          {/* Sensor Tag prompt — replaces the previous WIP notice. Hidden
              once the user has clicked on the model (pending pick captured)
              or once a sensor marker is selected. */}
          {tool === "sensor" && !isLoading && !loadError && !pendingSensorPick && !selectedSensorTag && !missNotice && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 rounded-full border border-cyan-500/40 px-4 py-2 text-xs text-cyan-200">
              <Radio className="h-3.5 w-3.5" />
              <span>Click the splat to place a sensor — or click an existing sensor marker for live data.</span>
            </div>
          )}

          {/* Surface-miss toast */}
          {missNotice && (
            <div className="liquid-glass absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border-amber-500/40 px-4 py-2 text-xs text-amber-200">
              {missNotice}
            </div>
          )}

          {/* Floating camera-control stack (bottom-right). One column,
              grouped by purpose: mode toggle → frame → help → zoom in/out →
              FOV. Sits right where the user's cursor naturally lives when
              they want to adjust the view. Hidden during loading/error/
              processing overlays so the splat-not-yet-here screens stay
              uncluttered. Wrapped in the project's liquid-glass surface so it
              picks up the same frosted look as modals and panels. */}
          {viewUrl && !loadError && !isLoading && twin?.status !== "processing" && (
            <div className="absolute bottom-4 right-4 z-20 flex flex-col gap-1 rounded-xl border border-white/15 bg-surface-950/95 p-1.5 shadow-xl backdrop-blur-md">
              <FloatingButton
                active={viewMode === "orbit"}
                onClick={() => setViewMode("orbit")}
                icon={Orbit}
                title="Orbit mode — drag rotates around the model (WASD pans)"
              />
              <FloatingButton
                active={viewMode === "fly"}
                onClick={() => setViewMode("fly")}
                icon={Plane}
                title="Fly mode — WASD + drag look"
              />
              <div className="mx-auto h-px w-5 bg-white/10" />
              <FloatingButton
                onClick={() => viewerRef.current?.goHome()}
                icon={Home}
                title="Home — place me back inside the scene (default view)"
              />
              <FloatingButton
                onClick={() => viewerRef.current?.recenter()}
                icon={Maximize2}
                title="Frame — re-fit camera to see the whole model"
              />
              <FloatingButton
                active={helpOpen}
                onClick={() => setHelpOpen((v) => !v)}
                icon={Keyboard}
                title="Show navigation help"
              />
              <div className="mx-auto h-px w-5 bg-white/10" />
              <FloatingButton
                onClick={() => viewerRef.current?.zoomIn()}
                icon={ZoomIn}
                title="Zoom in (or double-click on the model)"
              />
              <FloatingButton
                onClick={() => viewerRef.current?.zoomOut()}
                icon={ZoomOut}
                title="Zoom out"
              />
              <div className="mx-auto h-px w-5 bg-white/10" />
              {/* FOV slider — vertical, sits directly under the zoom buttons.
                  Lower FOV = telephoto/zoomed in, higher = wide-angle.
                  Bound to the camera via SparkViewer's setFov handle. */}
              <div
                className="flex flex-col items-center gap-1 py-1.5"
                title="Field of view"
              >
                <span className="text-[9px] font-semibold uppercase tracking-wider text-surface-300">
                  FOV
                </span>
                <input
                  type="range"
                  min={20}
                  max={110}
                  step={1}
                  value={fov}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setFov(v);
                    viewerRef.current?.setFov(v);
                  }}
                  className="fov-slider h-20 w-2"
                  style={{ writingMode: "vertical-lr", direction: "rtl" }}
                />
                <span className="text-[10px] font-mono text-surface-200">{fov}°</span>
              </div>
            </div>
          )}

          {/* Controls help overlay — mode-aware so users don't see keys that
              do nothing in the current mode. */}
          {helpOpen && (
            <div className="liquid-glass-strong absolute right-4 top-4 z-20 w-72 rounded-xl p-4 text-xs text-surface-300 shadow-2xl">
              <div className="mb-2 flex items-center justify-between">
                <span className="font-semibold text-surface-100">
                  {viewMode === "orbit" ? "Orbit mode" : "Fly mode"}
                </span>
                <button onClick={() => setHelpOpen(false)} className="text-surface-400 hover:text-surface-200">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {viewMode === "orbit" ? (
                <div className="space-y-1.5 font-mono text-[11px]">
                  <ControlRow keys="Left drag" desc="Orbit around target" />
                  <ControlRow keys="Right drag" desc="Pan target" />
                  <ControlRow keys="Scroll" desc="Dolly in / out" />
                  <ControlRow keys="Double-click" desc="Zoom toward that point" />
                  <ControlRow keys="W A S D" desc="Pan forward / strafe / back" />
                  <ControlRow keys="Q E" desc="Pan down / up" />
                  <ControlRow keys="Shift" desc="Sprint (5×)" />
                  <ControlRow keys="Ctrl" desc="Slow (0.2×)" />
                  <ControlRow keys="FOV" desc="Field of view slider" />
                  <ControlRow keys="Frame" desc="Re-fit to splat" />
                </div>
              ) : (
                <div className="space-y-1.5 font-mono text-[11px]">
                  <ControlRow keys="Left drag" desc="Look around" />
                  <ControlRow keys="Right drag" desc="Slide / pan" />
                  <ControlRow keys="Scroll" desc="Zoom" />
                  <ControlRow keys="Double-click" desc="Zoom toward that point" />
                  <ControlRow keys="W A S D" desc="Move forward / strafe / back" />
                  <ControlRow keys="Q E" desc="Move down / up" />
                  <ControlRow keys="Shift" desc="Sprint (5×)" />
                  <ControlRow keys="Ctrl" desc="Slow (0.2×)" />
                  <ControlRow keys="Frame" desc="Re-fit to splat" />
                </div>
              )}
              <div className="mt-3 border-t border-surface-700 pt-2 text-[10px] text-surface-500">
                {viewMode === "orbit"
                  ? "Orbit mode is best for inspecting a model. Switch to Fly mode to walk through the scene."
                  : "Fly mode is best for walking through a scene. Switch to Orbit mode to inspect a single model."}
                On touch devices a virtual joystick appears bottom-left.
              </div>
            </div>
          )}
        </div>

        {/* Side panel: Asset Tags / Sensor Tags tabs */}
        <aside className="hidden w-72 flex-shrink-0 border-l border-surface-700/50 bg-surface-900/40 lg:flex lg:flex-col">
          {/* Tab strip — the top toolbar's Asset Tag / Sensor Tag buttons
              also flip these tabs, so the sidebar always reflects the
              user's current intent. */}
          <div className="flex shrink-0 border-b border-surface-700/50 bg-surface-900/60">
            <SidebarTabButton
              active={sidebarTab === "assets"}
              onClick={() => setSidebarTab("assets")}
              icon={TagIcon}
              label="Asset Tags"
              count={tags.length}
            />
            <SidebarTabButton
              active={sidebarTab === "sensors"}
              onClick={() => setSidebarTab("sensors")}
              icon={Radio}
              label="Sensor Tags"
              count={sensorTags.length}
            />
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-3">
            {sidebarTab === "sensors" ? (
              <SensorTagsList
                tags={sensorTags}
                selectedTagId={selectedSensorTagId}
                deletingTagId={deletingSensorTagId}
                assets={siteAssets}
                onAssetChanged={refreshSensorTags}
                onOpenInInstrumentation={(sensorId) =>
                  navigate(`/instrumentation/sensors/${sensorId}`)
                }
                onSelect={(t) => {
                  // Toggle: clicking the already-selected row collapses it.
                  if (selectedSensorTagId === t.id) {
                    setSelectedSensorTagId(null);
                    return;
                  }
                  setSelectedSensorTagId(t.id);
                  const g = t.geometry as { x?: number; y?: number; z?: number };
                  if (
                    typeof g.x === "number" &&
                    typeof g.y === "number" &&
                    typeof g.z === "number"
                  ) {
                    viewerRef.current?.zoomToPoint(new THREE.Vector3(g.x, g.y, g.z));
                    const marker = sensorMarkersRef.current.get(t.id);
                    if (marker) viewerRef.current?.highlightMarker(marker);
                  }
                }}
                onDelete={async (t) => {
                  try {
                    setDeletingSensorTagId(t.id);
                    await deleteSensorTag(t.id);
                    if (selectedSensorTagId === t.id) setSelectedSensorTagId(null);
                    refreshSensorTags();
                  } finally {
                    setDeletingSensorTagId(null);
                  }
                }}
              />
            ) : tags.length === 0 ? (
              <p className="px-3 py-6 text-center text-xs text-surface-500">
                No asset tags yet. Switch to the Asset Tag tool and click on the splat.
              </p>
            ) : (
              <div className="space-y-2">
              {tags.map((t) => {
                const isDeleting = deletingTagId === t.id;
                const isExpanded = selectedTagId === t.id;
                // Compact view shows just one identifier (asset name preferred).
                // Expanded view shows asset name, asset code, and any label
                // separately — each on its own line so long values wrap
                // cleanly without truncation.
                const headline = t.asset_name || t.asset_code || t.label || "Tag";
                const maint = maintenanceByAsset.get(t.asset_id) ?? null;
                return (
                  <div
                    key={t.id}
                    className={
                      "group rounded-md border transition-colors " +
                      (isExpanded
                        ? "border-brand-500/50 bg-brand-500/10"
                        : "border-surface-700/60 bg-surface-900/60 hover:border-brand-500/40 hover:bg-brand-500/5")
                    }
                  >
                    {/* Header row — click to focus the tag in 3D AND expand
                        this card so the user sees the full asset
                        name/code/label. Selected state stays open until the
                        user clicks another tag. */}
                    <div className="flex items-center gap-2 px-2 py-1.5">
                      <button
                        onClick={() => {
                          setSelectedTagId(t.id);
                          focusTag(t);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                        title="Show details and zoom to this tag in the 3D model"
                      >
                        <span
                          className="h-2 w-2 shrink-0 rounded-full ring-1 ring-white/20"
                          style={{ backgroundColor: t.color || "#6366f1" }}
                        />
                        <span
                          className={
                            "min-w-0 flex-1 text-xs font-medium text-surface-100 " +
                            (isExpanded ? "break-words" : "truncate")
                          }
                        >
                          {headline}
                        </span>
                      </button>

                      {maint?.state && (
                        <MaintenanceDot
                          state={maint.state}
                          count={
                            maint.overdueCount +
                            maint.inProgressCount +
                            maint.scheduledCount
                          }
                        />
                      )}

                      <div className="flex shrink-0 items-center gap-0.5">
                        <SidebarIconButton
                          onClick={() => navigate(`/assets/${t.asset_id}`)}
                          title="Open linked asset in the register"
                          tone="open"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </SidebarIconButton>
                        {isAdmin && (
                          <SidebarIconButton
                            onClick={() => setTagBeingEdited(t)}
                            title="Edit this tag (asset, color, label)"
                            tone="edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </SidebarIconButton>
                        )}
                        {isAdmin && (
                          <SidebarIconButton
                            onClick={() => handleDeleteTag(t)}
                            disabled={isDeleting}
                            tone="danger"
                            title="Delete this tag (the asset stays in the register)"
                          >
                            {isDeleting ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="h-3.5 w-3.5" />
                            )}
                          </SidebarIconButton>
                        )}
                      </div>
                    </div>

                    {/* Expanded detail — only rendered for the selected tag.
                        Each field wraps freely (no truncate) so long asset
                        names and codes are fully readable. */}
                    {isExpanded && (
                      <div className="space-y-1 border-t border-brand-500/20 px-3 py-2 text-[11px]">
                        {t.asset_name && (
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
                              Asset
                            </div>
                            <div className="break-words text-surface-100">{t.asset_name}</div>
                          </div>
                        )}
                        {t.asset_code && (
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
                              Code
                            </div>
                            <div className="break-words font-mono text-surface-200">
                              {t.asset_code}
                            </div>
                          </div>
                        )}
                        {t.label && (
                          <div>
                            <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
                              Label
                            </div>
                            <div className="break-words text-surface-300">{t.label}</div>
                          </div>
                        )}
                        {maint?.state && (
                          <MaintenanceSummary summary={maint} />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Tag placement modal */}
      {twin && (
        <PlaceTagModal
          isOpen={pendingPick !== null}
          onClose={() => setPendingPick(null)}
          digitalTwinId={twin.id}
          virtualSiteId={twin.virtual_site_id}
          worldPoint={pendingPick}
          taggedAssetIds={taggedAssetIds}
          usedColors={usedTagColors}
          onCreated={() => {
            setPendingPick(null);
            refreshTags();
          }}
        />
      )}

      {/* Sensor tag placement modal */}
      {twin && (
        <PlaceSensorTagModal
          isOpen={pendingSensorPick !== null}
          onClose={() => setPendingSensorPick(null)}
          virtualSiteId={twin.virtual_site_id}
          digitalTwinId={twin.id}
          worldPoint={pendingSensorPick}
          taggedSensorIds={taggedSensorIds}
          onCreated={() => {
            setPendingSensorPick(null);
            refreshSensorTags();
          }}
        />
      )}

      {/* Live sensor readings render inline in the right sidebar's
          Sensor Tags tab (see SensorTagsList → InlineSensorReadout). No
          floating panel — keeps focus on the 3D model. */}

      {/* Tag edit modal */}
      {twin && (
        <EditTagModal
          isOpen={tagBeingEdited !== null}
          onClose={() => setTagBeingEdited(null)}
          tag={tagBeingEdited}
          virtualSiteId={twin.virtual_site_id}
          taggedAssetIds={taggedAssetIds}
          onSaved={() => {
            setTagBeingEdited(null);
            refreshTags();
          }}
        />
      )}

      {/* Delete-tag confirmation. Replaces window.confirm with an in-app
          styled dialog so the action matches the rest of the UI and so we
          can surface a clear error message if the API call fails. */}
      <ConfirmDialog
        isOpen={tagPendingDelete !== null}
        title="Delete tag?"
        message={
          tagPendingDelete
            ? `Delete the tag for ${tagPendingDelete.asset_code || tagPendingDelete.asset_name || tagPendingDelete.label || "this asset"}?`
            : ""
        }
        detail={
          tagDeleteError ??
          "This removes the 3D marker from the digital twin and breaks its link to the asset. The asset itself stays in the register."
        }
        confirmLabel="Delete tag"
        cancelLabel="Cancel"
        tone="destructive"
        isLoading={deletingTagId !== null}
        onConfirm={confirmTagDelete}
        onCancel={cancelTagDelete}
      />

      {/* Admin gate notice if non-admin tries to tag — actual block happens at API layer.
          We leave the UI accessible so non-admins can still navigate and read tags. */}
      {!isAdmin && tool === "tag" && (
        <div className="pointer-events-none absolute top-20 left-1/2 -translate-x-1/2 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-xs text-yellow-200">
          You have read-only access — tag creation requires site_admin or org_admin role.
        </div>
      )}
    </motion.div>
  );
}

/** Parse a tag color (e.g. "#a855f7" or "rgb(...)") to a THREE-compatible hex
 *  number. Returns the default brand purple when the input is null/invalid. */
function parseTagColor(input: string | null, fallback: number = 0xa855f7): number {
  if (!input) return fallback;
  const m = /^#([0-9a-fA-F]{6})$/.exec(input.trim());
  if (m) return parseInt(m[1], 16);
  return fallback;
}

/** Small icon button used in each sidebar tag card's action row.
 *  Each tone has its own permanent tint so the three actions are
 *  visually distinct at a glance — sky for "open elsewhere", amber for
 *  "edit", red for the destructive delete. */
/** Sidebar tab button used to flip between Asset Tags and Sensor Tags
 *  lists. Mirrors the look of the top toolbar's ToolButton at sidebar
 *  scale, with a count pill on the right. */
function SidebarTabButton({
  active,
  onClick,
  icon: Icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  label: string;
  count: number;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "flex flex-1 items-center justify-center gap-1.5 border-b-2 px-2 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors " +
        (active
          ? "border-brand-400 text-brand-200"
          : "border-transparent text-surface-400 hover:text-surface-200")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      <span
        className={
          "ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-bold " +
          (active
            ? "bg-brand-500/30 text-brand-100"
            : "bg-surface-800 text-surface-400")
        }
      >
        {count}
      </span>
    </button>
  );
}

function SidebarIconButton({
  onClick,
  title,
  children,
  disabled,
  tone = "neutral",
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  disabled?: boolean;
  tone?: "neutral" | "open" | "edit" | "danger";
}) {
  const toneClass = {
    neutral: "text-surface-400 hover:bg-surface-800 hover:text-surface-100",
    open: "text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 hover:text-sky-300",
    edit: "text-amber-400 bg-amber-500/10 hover:bg-amber-500/20 hover:text-amber-300",
    danger: "text-red-400 bg-red-500/10 hover:bg-red-500/20 hover:text-red-300",
  }[tone];
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      disabled={disabled}
      title={title}
      className={
        "flex h-6 w-6 items-center justify-center rounded-md transition-colors disabled:opacity-50 " +
        toneClass
      }
    >
      {children}
    </button>
  );
}

/** Compact icon button used in the bottom-right floating control stack. */
function FloatingButton({
  active,
  onClick,
  icon: Icon,
  title,
}: {
  active?: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  title: string;
}) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={
        "flex h-8 w-8 items-center justify-center rounded-md transition-colors " +
        (active
          ? "bg-brand-500/20 text-brand-200"
          : "text-surface-300 hover:bg-surface-800 hover:text-surface-100")
      }
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}

/** Full-canvas loading overlay shown while the splat is being fetched and
 *  rendered for the first time. Bridges the gap between "metadata loaded"
 *  and "first 3D frame visible" — previously the user saw nothing for a few
 *  seconds and assumed the viewer was broken. Shows a real percentage when
 *  the server provides Content-Length, falls back to an animated shimmer
 *  bar otherwise. */
function LoadingOverlay({
  phase,
  loaded,
  total,
}: {
  phase: "metadata" | "connecting" | "downloading" | "parsing";
  loaded: number;
  total: number;
}) {
  const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : null;
  const mb = (b: number) => (b / 1024 / 1024).toFixed(1);
  const title =
    phase === "metadata"
      ? "Loading digital twin…"
      : phase === "connecting"
        ? "Connecting to storage…"
        : phase === "downloading"
          ? "Streaming 3D model"
          : "Preparing for render…";
  const subtitle =
    phase === "downloading"
      ? total > 0
        ? `${mb(loaded)} MB / ${mb(total)} MB`
        : `${mb(loaded)} MB loaded`
      : phase === "parsing"
        ? "Almost there — building the splat tree"
        : phase === "connecting"
          ? "Reaching the storage bucket"
          : "Fetching metadata and access tokens";
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-950/95">
      <div className="w-full max-w-sm px-8">
        <div className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-brand-300" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-surface-100">{title}</div>
            <div className="text-xs text-surface-400">{subtitle}</div>
          </div>
          {pct !== null && (
            <div className="ml-auto font-mono text-sm font-semibold text-brand-200">
              {pct}%
            </div>
          )}
        </div>
        <div className="relative mt-3 h-2 overflow-hidden rounded-full bg-surface-800/80 ring-1 ring-white/5">
          {pct !== null ? (
            <div
              className="h-full rounded-full bg-gradient-to-r from-brand-500 via-purple-500 to-brand-400 transition-[width] duration-150 ease-out"
              style={{
                width: `${pct}%`,
                boxShadow: "0 0 12px rgba(99,102,241,0.55)",
              }}
            />
          ) : (
            <div className="loading-shimmer absolute inset-0 rounded-full bg-gradient-to-r from-transparent via-brand-500/70 to-transparent" />
          )}
        </div>
      </div>
    </div>
  );
}

/** Floating leader-line + label that follows a selected tag's 3D marker as
 *  the camera moves. Owns its own rAF projection loop and writes to the DOM
 *  via refs (no React state) so it doesn't re-render the parent viewer 60
 *  times per second. */
function TagLabelOverlay({
  tag,
  viewerRef,
  maintenance,
}: {
  tag: DigitalTwinTag;
  viewerRef: RefObject<SparkViewerHandle | null>;
  maintenance: AssetMaintenanceSummary | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<SVGLineElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  const tipRef = useRef<SVGCircleElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const LEADER_DX = 70;
  const LEADER_DY = -52;

  const accent = tag.color || "#a5b4fc";
  const labelName = tag.asset_name || tag.asset_code || tag.label || "Tag";

  useEffect(() => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
      return;
    }
    const worldPoint = new THREE.Vector3(x, y, z);
    let raf = 0;
    const tick = () => {
      const proj = viewerRef.current?.projectToScreen(worldPoint);
      const container = containerRef.current;
      if (proj && container) {
        if (proj.visible) {
          container.style.opacity = "1";
          const dotX = proj.x;
          const dotY = proj.y;
          const tipX = dotX + LEADER_DX;
          const tipY = dotY + LEADER_DY;
          lineRef.current?.setAttribute("x1", String(dotX));
          lineRef.current?.setAttribute("y1", String(dotY));
          lineRef.current?.setAttribute("x2", String(tipX));
          lineRef.current?.setAttribute("y2", String(tipY));
          dotRef.current?.setAttribute("cx", String(dotX));
          dotRef.current?.setAttribute("cy", String(dotY));
          tipRef.current?.setAttribute("cx", String(tipX));
          tipRef.current?.setAttribute("cy", String(tipY));
          if (labelRef.current) {
            labelRef.current.style.transform = `translate(${tipX + 6}px, ${tipY - 14}px)`;
          }
        } else {
          container.style.opacity = "0";
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tag, viewerRef]);

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-20 transition-opacity duration-150"
      style={{ opacity: 0 }}
    >
      <svg
        className="absolute inset-0 h-full w-full"
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.6))" }}
      >
        <line
          ref={lineRef}
          stroke={accent}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        {/* Anchor disc at the world-space tag position. Larger than the
            leader's bend tip so the "this is where on the model the tag
            sits" point is visually dominant. White outline keeps it
            readable against any splat / cloud color. */}
        <circle ref={dotRef} r="6" fill={accent} stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
        <circle ref={tipRef} r="3" fill={accent} stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
      </svg>
      <div
        ref={labelRef}
        className="absolute left-0 top-0 flex flex-col items-start gap-1 will-change-transform"
      >
        <div
          className="rounded-md border-2 bg-surface-950 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-surface-50 shadow-lg"
          style={{
            borderColor: accent,
            boxShadow: `0 0 14px ${accent}55, 0 4px 14px rgba(0,0,0,0.7)`,
          }}
        >
          {labelName}
        </div>
        {maintenance?.state && maintenance.primaryTask && (
          <div
            className={
              "rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider shadow-lg " +
              MAINTENANCE_STATE_TOKENS[maintenance.state].chipClass
            }
          >
            {MAINTENANCE_STATE_TOKENS[maintenance.state].label}
            {" · "}
            {formatDate(maintenance.primaryTask.scheduled_date)}
          </div>
        )}
      </div>
    </div>
  );
}

/** Floating leader-line + label for the selected SENSOR tag. Mirrors
 *  TagLabelOverlay above (the asset-tag version) — same SVG line + label
 *  pattern, same rAF projection loop — but pulls fields off the sensor
 *  tag's denormalized record and uses the sensor's color as the accent.
 *  Kept as a sibling component rather than a generic one so each tag
 *  type can evolve its on-canvas affordances independently (asset tags
 *  may grow maintenance chips, sensor tags may grow live-value chips). */
function SensorTagLabelOverlay({
  tag,
  viewerRef,
}: {
  tag: SensorTag;
  viewerRef: RefObject<SparkViewerHandle | null>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<SVGLineElement>(null);
  const dotRef = useRef<SVGCircleElement>(null);
  const tipRef = useRef<SVGCircleElement>(null);
  const labelRef = useRef<HTMLDivElement>(null);

  const LEADER_DX = 70;
  const LEADER_DY = -52;

  // Sensor tags default to cyan if no color set, distinct from asset
  // tags' indigo default — keeps the two layers visually separable.
  const accent = tag.color || "#22d3ee";
  const labelName = tag.label || tag.sensor_name || "Sensor";

  useEffect(() => {
    const g = (tag.geometry || {}) as Record<string, unknown>;
    const x = g.x as number, y = g.y as number, z = g.z as number;
    if (typeof x !== "number" || typeof y !== "number" || typeof z !== "number") {
      return;
    }
    const worldPoint = new THREE.Vector3(x, y, z);
    let raf = 0;
    const tick = () => {
      const proj = viewerRef.current?.projectToScreen(worldPoint);
      const container = containerRef.current;
      if (proj && container) {
        if (proj.visible) {
          container.style.opacity = "1";
          const dotX = proj.x;
          const dotY = proj.y;
          const tipX = dotX + LEADER_DX;
          const tipY = dotY + LEADER_DY;
          lineRef.current?.setAttribute("x1", String(dotX));
          lineRef.current?.setAttribute("y1", String(dotY));
          lineRef.current?.setAttribute("x2", String(tipX));
          lineRef.current?.setAttribute("y2", String(tipY));
          dotRef.current?.setAttribute("cx", String(dotX));
          dotRef.current?.setAttribute("cy", String(dotY));
          tipRef.current?.setAttribute("cx", String(tipX));
          tipRef.current?.setAttribute("cy", String(tipY));
          if (labelRef.current) {
            labelRef.current.style.transform = `translate(${tipX + 6}px, ${tipY - 14}px)`;
          }
        } else {
          container.style.opacity = "0";
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tag, viewerRef]);

  return (
    <div
      ref={containerRef}
      className="pointer-events-none absolute inset-0 z-20 transition-opacity duration-150"
      style={{ opacity: 0 }}
    >
      <svg
        className="absolute inset-0 h-full w-full"
        style={{ filter: "drop-shadow(0 0 4px rgba(0,0,0,0.6))" }}
      >
        <line ref={lineRef} stroke={accent} strokeWidth="1.5" strokeLinecap="round" />
        {/* Anchor disc at the world-space tag position. Larger than the
            leader's bend tip so the "this is where on the model the tag
            sits" point is visually dominant. White outline keeps it
            readable against any splat / cloud color. */}
        <circle ref={dotRef} r="6" fill={accent} stroke="rgba(255,255,255,0.85)" strokeWidth="1.5" />
        <circle ref={tipRef} r="3" fill={accent} stroke="rgba(255,255,255,0.7)" strokeWidth="1" />
      </svg>
      <div
        ref={labelRef}
        className="absolute left-0 top-0 flex flex-col items-start gap-1 will-change-transform"
      >
        <div
          className="rounded-md border-2 bg-surface-950 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-surface-50 shadow-lg"
          style={{
            borderColor: accent,
            boxShadow: `0 0 14px ${accent}55, 0 4px 14px rgba(0,0,0,0.7)`,
          }}
        >
          {labelName}
        </div>
      </div>
    </div>
  );
}

function ControlRow({ keys, desc }: { keys: string; desc: string }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="rounded bg-surface-800 px-1.5 py-0.5 font-mono text-[10px] text-brand-300">
        {keys}
      </span>
      <span className="flex-1 text-surface-400">{desc}</span>
    </div>
  );
}

/** Compact maintenance state chip shown on each sidebar tag row. The dot's
 *  color encodes the worst active state (overdue → red, in_progress → blue,
 *  scheduled → amber) and the count is the total active task count. Hidden
 *  when the asset has no active maintenance — no chip means "nothing to do." */
function MaintenanceDot({
  state,
  count,
}: {
  state: "overdue" | "in_progress" | "scheduled";
  count: number;
}) {
  const tokens = MAINTENANCE_STATE_TOKENS[state];
  return (
    <span
      className={
        "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider " +
        tokens.chipClass
      }
      title={`${tokens.label}: ${count} active task${count === 1 ? "" : "s"}`}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ backgroundColor: tokens.hex }}
      />
      {count}
    </span>
  );
}

/** Expanded-detail maintenance section. Lists per-bucket counts and surfaces
 *  the single most-urgent active task (description + scheduled date). */
function MaintenanceSummary({ summary }: { summary: AssetMaintenanceSummary }) {
  if (!summary.state) return null;
  const tokens = MAINTENANCE_STATE_TOKENS[summary.state];
  const t = summary.primaryTask;
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
        Maintenance
      </div>
      <div className="mt-1 space-y-1">
        <span
          className={
            "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider " +
            tokens.chipClass
          }
        >
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ backgroundColor: tokens.hex }}
          />
          {tokens.label}
        </span>
        <div className="text-[10px] text-surface-400">
          {summary.overdueCount > 0 && (
            <span className="text-red-300">
              {summary.overdueCount} overdue
            </span>
          )}
          {summary.overdueCount > 0 &&
            (summary.inProgressCount > 0 || summary.scheduledCount > 0) &&
            " · "}
          {summary.inProgressCount > 0 && (
            <span className="text-blue-300">
              {summary.inProgressCount} in progress
            </span>
          )}
          {summary.inProgressCount > 0 && summary.scheduledCount > 0 && " · "}
          {summary.scheduledCount > 0 && (
            <span className="text-amber-300">
              {summary.scheduledCount} scheduled
            </span>
          )}
        </div>
        {t && (
          <div className="rounded border border-surface-700/60 bg-surface-900/50 px-2 py-1">
            <div className="break-words text-[11px] text-surface-200">
              {t.description}
            </div>
            <div className="mt-0.5 text-[10px] text-surface-500">
              Due {formatDate(t.scheduled_date)}
              {t.priority && ` · ${t.priority}`}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ToolButton({
  active, onClick, icon: Icon, label, wip,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof MousePointer;
  label: string;
  /** Marks the tool as work-in-progress: still selectable, but renders a
   *  small amber WIP pip so users know the feature isn't fully wired up. */
  wip?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={
        "relative flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors " +
        (active
          ? "bg-brand-500/20 text-brand-200"
          : "text-surface-400 hover:bg-surface-800 hover:text-surface-200")
      }
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      {wip && (
        <span
          className="ml-0.5 rounded-full border border-amber-500/40 bg-amber-500/15 px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider text-amber-300"
          title="Work in progress"
        >
          WIP
        </span>
      )}
    </button>
  );
}
